import { Injectable } from '@angular/core';
// Rx JS imports
import { Observable } from 'rxjs';
import { map, first, take } from 'rxjs/operators';
import { combineLatest, from } from 'rxjs';

// Models
import { IconSet, IconSpriteInfo, ICON_SPRITE_SHEET_DB, MapboxSprite, SpriteImage } from '../models/icon-set';
import { ProjectService } from './project.service';

import { ListenEvent } from './database';
import { DatabaseService } from './database.service';
import { MediaService } from './media.service';
import { MotesLoginService } from '../motes-login';
import { HttpClient } from '@angular/common/http';
import { MotesPaths, MoteState } from '../models';

@Injectable({ providedIn: 'root' })
export class IconService {

    private imageList: SpriteImage[] = null;

    constructor(private af: DatabaseService,
        private projectService: ProjectService,
        private mediaService: MediaService,
        private loginService: MotesLoginService,
        private http: HttpClient) {
    }

    public init() {
    }

    public saveIconSet(iconSet: IconSet): Promise<any> {
        // Get new id for mote
        if(!iconSet.key) {
            // Generate new key
            iconSet.key = this.af.database.ref().push().key;
            iconSet.createdOn = this.af.serverTime();
        }
        // Create database reference and update moteContent
        return this.af.database.ref('projectData/' + iconSet.project + "/iconSets/" + iconSet.key).update(iconSet.toJson());
    }

    public deleteIconSet(iconSet: IconSet): Promise<boolean> {
        if(!iconSet.key || !iconSet.project) {
            console.error("Cannot delete icon set ", iconSet);
            return Promise.reject(false);
        }
        this.mediaService.deleteFiles(iconSet.moteLocked.files);
        this.mediaService.deleteFiles(iconSet.moteAvailable.files);
        this.mediaService.deleteFiles(iconSet.moteUnlocked.files);
        this.mediaService.deleteFiles(iconSet.moteUser.files);
        this.mediaService.deleteFiles(iconSet.moteInactive.files);

        return this.af.database.ref('projectData/' + iconSet.project + "/iconSets/" + iconSet.key).remove().then(() => true).catch(() => false);
    }

    /**
     * Watch the icon sets of a project. 
     * Obs: Function should only be used when generating a icon set sprite sheet -
     * use the loadSpriteSheetCanvas to load all icons in sprite sheet
     */
    public watchPojectIconSets(projectKey: string): Observable<IconSet[]> {
        const CHANGE_EVENTS: Array<ListenEvent> = [ListenEvent.added, ListenEvent.removed, ListenEvent.changed, ListenEvent.moved];
        // Get new list observable
        let projectIcons = this.af.list('projectData/' + projectKey + "/iconSets/").snapshotChanges(CHANGE_EVENTS).pipe(map((results) => {
            // Create array of Project objects from firebase object array
            return results.map((snap) => {
                return new IconSet(snap.key, snap.payload.val());
            });
        }));
        return projectIcons;
    }

    /**
     * Watch icon sets of all projects and combine in obe observable. 
     * Obs: Functions should only be used when generating a icon set sprite sheet -
     * use the loadSpriteSheetCanvas to load all icons in sprite sheet
     */
    public watchIconSets(projectKey: string, listAll: boolean = false): Observable<IconSet[]> {
        // Get new list observable
        let projectIcons = this.watchPojectIconSets(projectKey);

        // Only return styles for default project
        if(projectKey == "default") return listAll ? from(this.getAllIconSets()) : projectIcons;

        // Get default Motes styles from DB
        const CHANGE_EVENTS: Array<ListenEvent> = [ListenEvent.added, ListenEvent.removed, ListenEvent.changed, ListenEvent.moved];
        let defaultIcons = this.af.list("projectData/default/iconSets/").snapshotChanges(CHANGE_EVENTS).pipe(map((results) => {
            // Create array of Project objects from firebase object array
            return results.map((snap) => {
                return new IconSet(snap.key, snap.payload.val());
            });
        }));

        return combineLatest(defaultIcons, projectIcons).pipe(map(([defIcons, proIcons]) => {
            return [...defIcons, ...proIcons];
        }));
    }

    public watchIconSet(projectKey: string, iconSetKey: string): Observable<IconSet> {
        // Get mote from DB and mapt to Mote object
        return this.af.object('projectData/' + projectKey + "/iconSets/" + iconSetKey).snapshotChanges().pipe(map((json) => {
            return new IconSet(iconSetKey, json.payload.val());
        }));
    }

    public getAllIconSets(): Promise<IconSet[]> {
        // Get all project keys
        return new Promise(async (resolve) => {
            let projectKeys = await this.projectService.getAllProjectKeys();
            let iconPromiseList = [];

            for(let projectKey of projectKeys) {
                let iconPromise = this.watchPojectIconSets(projectKey).pipe(first()).toPromise();
                iconPromiseList.push(iconPromise);
            }

            Promise.all(iconPromiseList).then(iconSets => {
                let merged = [].concat(...iconSets);
                resolve(merged);
            });
        });
    }

    public generateKey(): string {
        return this.af.database.ref().push().key;
    }


    public getIconSpriteSheet(): Promise<IconSpriteInfo> {
        // Get mote from DB and mapt to Mote object
        return this.af.object<IconSpriteInfo>(ICON_SPRITE_SHEET_DB).valueChanges().pipe(take(1)).toPromise();
    }

    public async generateIconSpriteSheet(): Promise<{ status: string, data: IconSpriteInfo }> {
        let isAdmin = this.loginService.userRights.isAdmin || this.loginService.userRights.isSuperAdmin;
        if(!isAdmin) return Promise.reject("User does not have rights to update sprite sheet?");

        // Get Authorization id token
        let options = await this.loginService.getAuthOptions();
        return this.http.post<{ status: string, data: IconSpriteInfo }>(MotesPaths.MOTES_REST + "/api/generate-icon-sheet", null, options).pipe(first()).toPromise();
    }

    public async loadSpriteSheetCanvas(): Promise<[MapboxSprite[], CanvasRenderingContext2D]> {
        // Get sprite sheet info from database
        let spriteInfo = await this.getIconSpriteSheet();

        // Load sprite sheet image
        let image = new Image();
        image.crossOrigin = "Anonymous";
        image.src = spriteInfo.imageUrl;
        // Wait for image to load
        await new Promise((resolve) => image.onload = resolve);
        image.onerror = (err) => { console.warn("Could not load icon sprite sheet: " + err) };

        // Create a canvas
        let canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;

        // Draw sprite sheet in canvas
        let ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0);

        let spriteList = Object.keys(spriteInfo.spriteSheet).map(key => {
            let sprite = spriteInfo.spriteSheet[key];
            sprite.key = key;
            return sprite;
        });

        return [spriteList, ctx];
    }

    public loadSpriteSheetIcons(spriteList: MapboxSprite[], canvas: CanvasRenderingContext2D): SpriteImage[] {
        // Load all icons from sprite sheet
        let images = spriteList.map((sprite) => {
            // Extract SpriteImage from canvas
            return {
                // Save sprite data
                sprite: sprite,
                // Extract image data from canvas
                image: canvas.getImageData(sprite.x, sprite.y, sprite.width, sprite.height)
            }
        });
        return images;
    }

    public async loadCachedSpriteSheetIcons(): Promise<SpriteImage[]> {
        // Return cached image list if available
        if(this.imageList) return this.imageList;
        // Load sprite list
        let [sprites, ctx] = await this.loadSpriteSheetCanvas();
        // Parse and load images from canvas
        this.imageList = this.loadSpriteSheetIcons(sprites, ctx);
        return this.imageList;
    }

    public async getIcon(iconSetKey: string, state: MoteState): Promise<string> {
        // Load cached image list
        let spriteImage = await this.loadCachedSpriteSheetIcons();
        // Generate icon key        
        let iconKey = `${iconSetKey}_${IconService.moteStateToIconId(state)}`;
        // Find icon in icon set
        let imageData = spriteImage.find(icon => icon.sprite.key == iconKey);
        // Convert imageData to imageUrl
        return imageData ? IconService.imageToUrl(imageData.image) : '';
    }

    private static imageToUrl(imageData: ImageData): string {
        // Create a canvas
        let canvas = document.createElement('canvas');
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        // Draw sprite sheet in canvas
        let ctx = canvas.getContext("2d");
        // Put image data on canvas
        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL();
    }

    private static moteStateToIconId(state: MoteState): string {
        let str = state.toLowerCase() || '';
        // Capitalize state name and add 'mote' in front
        return 'mote' + str.charAt(0).toUpperCase() + str.slice(1);
    }
}
